// src/App.js
import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import './App.css';

function App() {
  const [uploadedFileLink, setUploadedFileLink] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [previousFiles, setPreviousFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load previous files on component mount
  useEffect(() => {
    fetchPreviousFiles();
  }, []);

  const fetchPreviousFiles = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('https://lottie.motioncube.in/api/files');
      if (!response.ok) {
        throw new Error('Failed to fetch files');
      }
      const data = await response.json();
      setPreviousFiles(data.files || []);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Error loading previous files: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    
    // Check if file is a JSON or lottie file
    if (!file.name.endsWith('.json') && !file.name.endsWith('.lottie')) {
      setError('Please upload only .json or .lottie files');
      return;
    }

    setIsUploading(true);
    setError('');
    setUploadedFileLink('');

    const formData = new FormData();
    formData.append('lottieFile', file);

    try {
      const response = await fetch('https://lottie.motioncube.in/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload file');
      }

      const data = await response.json();
      setUploadedFileLink(data.previewLink);
      
      // Refresh the list of files
      fetchPreviousFiles();
    } catch (err) {
      setError('Error uploading file: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const copyToClipboard = (link) => {
    navigator.clipboard.writeText(link);
    alert('Link copied to clipboard!');
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="container">
      <h1>Lottie File Uploader</h1>
      
      <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the Lottie file here...</p>
        ) : (
          <p>Drag and drop a Lottie file (.json or .lottie) here, or click to select a file</p>
        )}
      </div>

      {isUploading && <p className="status">Uploading...</p>}
      {error && <p className="error">{error}</p>}

      {uploadedFileLink && (
        <div className="result">
          <h2>Your Lottie animation is ready!</h2>
          <div className="link-container">
            <input 
              type="text" 
              value={uploadedFileLink} 
              readOnly 
              className="link-input" 
            />
            <button onClick={() => copyToClipboard(uploadedFileLink)} className="copy-btn">Copy</button>
          </div>
          <div className="preview">
            <h3>Preview:</h3>
            <iframe 
              src={uploadedFileLink} 
              title="Lottie Preview" 
              width="400" 
              height="400" 
              frameBorder="0"
            />
          </div>
          <a 
            href={uploadedFileLink} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="preview-btn"
          >
            Open in new tab
          </a>
        </div>
      )}

      <div className="files-section">
        <h2>Previous Uploads</h2>
        
        {isLoading ? (
          <p className="loading-text">Loading previous files...</p>
        ) : previousFiles.length === 0 ? (
          <p className="no-files">No files have been uploaded yet.</p>
        ) : (
          <div className="files-list">
            {previousFiles.map((file) => (
              <div key={file.fileId} className="file-item">
                <div className="file-info">
                  <h3 className="file-name">{file.originalName}</h3>
                  <p className="file-meta">
                    <span className="file-date">{formatDate(file.uploadDate)}</span> • 
                    <span className="file-size">{formatFileSize(file.fileSize)}</span>
                  </p>
                </div>
                <div className="file-actions">
                  <a 
                    href={file.previewLink} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="view-btn"
                  >
                    View
                  </a>
                  <button 
                    onClick={() => copyToClipboard(`https://lottie.motioncube.in${file.previewLink}`)} 
                    className="copy-link-btn"
                  >
                    Copy Link
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;